'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { RothkoShader } from '@/components/effects/RothkoShader'

const stats = [
  { label: 'Auction Lots', value: 85, suffix: 'M' },
  { label: 'Fine Art Records', value: 18, suffix: 'M' },
  { label: 'Artists Tracked', value: 420, suffix: 'K+' },
  { label: 'Auction Houses', value: 4800, suffix: '+' },
  { label: 'Years of Data', value: 50, suffix: '+' },
]

function AnimatedCounter({ value, suffix }: { value: number; suffix: string }) {
  const [count, setCount] = useState(0)
  const ref = useRef<HTMLSpanElement>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0.5 }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!inView) return

    const duration = 2000
    const steps = 60
    const stepTime = duration / steps
    const increment = value / steps

    let current = 0
    const timer = setInterval(() => {
      current += increment
      if (current >= value) {
        setCount(value)
        clearInterval(timer)
      } else {
        setCount(current)
      }
    }, stepTime)

    return () => clearInterval(timer)
  }, [inView, value])

  const displayValue = Math.round(count).toLocaleString()

  return (
    <span ref={ref} className="tabular-nums">
      {displayValue}{suffix}
    </span>
  )
}

export function Hero() {
  return (
    <section className="rothko-hero">
      {/* WebGL Rothko color fields */}
      <RothkoShader />

      {/* Content */}
      <div className="relative z-10 flex flex-col justify-center min-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-7xl mx-auto w-full px-6"
        >
          {/* Eyebrow */}
          <span
            className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4"
            style={{
              background: 'rgba(255,255,255,0.1)',
              color: 'var(--accent-cream)',
              border: '1px solid rgba(255,255,255,0.15)',
            }}
          >
            Art Market Intelligence
          </span>

          {/* Headline */}
          <h1
            className="font-display text-5xl md:text-7xl lg:text-8xl font-light tracking-tight mb-4"
            style={{ color: 'var(--accent-cream)' }}
          >
            See the market before it moves.
          </h1>

          {/* Subtext */}
          <p
            className="text-lg md:text-xl max-w-2xl mb-4"
            style={{ color: 'rgba(255,255,255,0.7)' }}
          >
            For those who act on information, not intuition.
          </p>
          <p
            className="text-base md:text-lg max-w-2xl mb-10"
            style={{ color: 'rgba(255,255,255,0.5)' }}
          >
            Our AI synthesizes 50 years of market history—and every upcoming lot—into insights that matter: pricing confidence, timing signals, comparable analysis before the hammer falls.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <Link
              href="/app/market/contemporary"
              className="pill px-8 py-4 text-base font-medium"
              style={{
                background: 'var(--accent-cream)',
                color: 'var(--hero-ground)',
              }}
            >
              Explore the data
            </Link>
            <Link
              href="/get-started"
              className="flex items-center gap-2 px-6 py-4 text-base font-medium transition-opacity hover:opacity-80"
              style={{ color: 'var(--accent-cream)' }}
            >
              <svg
                className="w-5 h-5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
              Watch overview
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Stats strip */}
      <div className="stats-strip z-20">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.1, duration: 0.5 }}
                className="text-center"
              >
                <div
                  className="text-3xl md:text-4xl font-display font-light mb-1"
                  style={{ color: 'var(--accent-cream)' }}
                >
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                </div>
                <div
                  className="text-sm"
                  style={{ color: 'rgba(255,255,255,0.5)' }}
                >
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
